import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetOne, useNotify, useRefresh } from 'react-admin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import { DrawerState, useDrawer } from '@app/misc/DrawerContext';
import type { StudyFormId } from '@wwf/sdk';
import { StudyFormDrawerForm } from './StudyFormDrawerForm';
import { StudyFormInput } from '@domain/studyForm';

type StudyFormDrawerMode = 'create' | 'edit' | 'show' | 'version';

export function useStudyFormDrawer() {
    const drawer = useDrawer();
    const openDrawer = useCallback(
        (mode: StudyFormDrawerMode, selectedId: StudyFormId | null) =>
            drawer.openDrawer(state => (
                <StudyFormDrawer
                    {...state}
                    mode={mode}
                    selectedId={selectedId}
                />
            )),
        [drawer]
    );
    const closeDrawer = useCallback(() => {
        drawer.closeDrawer();
    }, [drawer]);
    return { openDrawer, closeDrawer };
}

export function StudyFormDrawer({
    open,
    onClose,
    mode,
    selectedId,
}: DrawerState & {
    mode: StudyFormDrawerMode;
    selectedId: StudyFormId | null;
}) {
    const notify = useNotify();
    const refresh = useRefresh();

    const [viewMode, setViewMode] = useState<StudyFormDrawerMode>(mode);

    useEffect(() => {
        if (open) {
            setViewMode(mode);
        }
    }, [open, mode, selectedId]);

    const title = useMemo(() => {
        if (viewMode === 'create') return 'Create study form';
        if (viewMode === 'version') return 'Create study form version';
        if (viewMode === 'show') return 'Study form';
        return 'Edit study form';
    }, [viewMode]);

    const loadRecord =
        viewMode === 'edit' || viewMode === 'show' || viewMode === 'version';

    const { data: selectedRecord, isPending: isPendingSelectedRecord } =
        useGetOne<StudyFormInput>(
            'studyForm',
            { id: selectedId ?? undefined },
            { enabled: open && loadRecord && !!selectedId }
        );

    const canToggleShowEdit =
        !!selectedId && (viewMode === 'edit' || viewMode === 'show');

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    gap={1}
                >
                    <Typography variant="h6">{title}</Typography>
                    {canToggleShowEdit &&
                        (viewMode === 'show' ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => setViewMode('edit')}
                            >
                                Edit
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setViewMode('show')}
                            >
                                View
                            </Button>
                        ))}
                </Stack>
                <StudyFormDrawerForm
                    mode={viewMode}
                    record={loadRecord ? selectedRecord : undefined}
                    loading={loadRecord && isPendingSelectedRecord}
                    onClose={onClose}
                    onSuccess={() => {
                        refresh();
                        notify('Saved', { type: 'info' });
                    }}
                />
            </Box>
        </Box>
    );
}
